// frontend/src/pages/dashboard/accounts/LinkedInModals.js
import React from 'react';
import { Modal, Button } from 'react-bootstrap';

/**
 * LinkedInModals handles:
 *   1) LN Unified Modal (selecting personal & page together)
 *   2) LN Delete Personal Modal
 *   3) LN Confirm Deletion Modal
 *
 * Props from Accounts.js:
 *
 *   // LN unified:
 *   showLnUnifiedModal, setShowLnUnifiedModal,
 *   lnAccounts, lnPages, selectedLnCombined,
 *   handleLnUnifiedCheckbox, handleConnectSelectedLnUnified,
 *   handleCloseLnUnifiedModal,
 *
 *   // LN delete personal:
 *   showLnDeleteModal, setShowLnDeleteModal,
 *   showLnDeleteConfirmModal, setShowLnDeleteConfirmModal,
 *   setSelectedLnCombined,
 *   handleDeleteLnConfirm,
 *
 *   // Optionally the entire accounts array
 *   accounts,
 */
export default function LinkedInModals({
  // LN unified
  showLnUnifiedModal,
  setShowLnUnifiedModal,
  lnAccounts,
  lnPages,
  selectedLnCombined,
  handleLnUnifiedCheckbox,
  handleConnectSelectedLnUnified,
  handleCloseLnUnifiedModal,

  // LN delete personal
  showLnDeleteModal,
  setShowLnDeleteModal,
  showLnDeleteConfirmModal,
  setShowLnDeleteConfirmModal,
  setSelectedLnCombined,
  handleDeleteLnConfirm,

  // Optionally the entire accounts array
  accounts,
}) {
  /**
   * 1) Build a combined array of personal & page items,
   *    but unify the fields so they both have lnId, displayName, and profileImage.
   */
  const combinedLn = [
    // Personal accounts
    ...lnAccounts.map((acc) => ({
      type: 'personal',
      lnId: acc.lnId,
      displayName: acc.displayName,
      profileImage: acc.profileImage || '',
    })),
    // Pages
    ...lnPages.map((pg) => ({
      type: 'page',
      lnId: pg.id,              // unify the key as lnId
      displayName: pg.name,     // unify displayName
      profileImage: pg.logoUrl || '', // unify profileImage
    })),
  ];

  /**
   * 2) If referencing LN personal from the main "accounts" array for deletion:
   */
  const lnAcc = accounts?.find((a) => a.platform.toLowerCase() === 'linkedin');
  const personalOnly = lnAcc
    ? lnAcc.connectedPages.filter((p) => p.lnId) // personal LN entries have lnId
    : [];

  return (
    <>
      {/* ************** LN Unified Modal ************** */}
      <Modal show={showLnUnifiedModal} onHide={handleCloseLnUnifiedModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Select LinkedIn Profiles &amp; Pages</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {combinedLn.length === 0 ? (
            <p>No LinkedIn profiles or pages found.</p>
          ) : (
            <ul className="list-group">
              {combinedLn.map((item) => {
                // We use item.lnId as the key for both personal & page
                const keyVal = item.lnId;
                const checked = selectedLnCombined.includes(keyVal);

                return (
                  <li
                    key={keyVal}
                    className="list-group-item d-flex align-items-center"
                  >
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      checked={checked}
                      onChange={() => handleLnUnifiedCheckbox(keyVal)}
                    />

                    {item.profileImage && (
                      <img
                        src={item.profileImage}
                        alt={item.displayName}
                        style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          marginRight: '8px',
                        }}
                      />
                    )}

                    <span>{item.displayName}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseLnUnifiedModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConnectSelectedLnUnified}>
            Connect
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ************** LN Delete Personal Modal ************** */}
      <Modal show={showLnDeleteModal} onHide={() => setShowLnDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete LinkedIn Personal Accounts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(() => {
            if (!lnAcc || lnAcc.connectedPages.length === 0) {
              return <p>No connected LinkedIn accounts to delete.</p>;
            }
            if (personalOnly.length === 0) {
              return <p>No personal LinkedIn accounts to delete.</p>;
            }
            return (
              <ul className="list-group">
                {personalOnly.map((item) => {
                  const checked = selectedLnCombined.includes(item.lnId);
                  return (
                    <li
                      key={item.lnId}
                      className="list-group-item d-flex align-items-center"
                    >
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={checked}
                        onChange={() => {
                          if (checked) {
                            // remove from array
                            const newArr = selectedLnCombined.filter(
                              (x) => x !== item.lnId
                            );
                            setSelectedLnCombined(newArr);
                          } else {
                            // add to array
                            setSelectedLnCombined([
                              ...selectedLnCombined,
                              item.lnId,
                            ]);
                          }
                        }}
                      />
                      {item.profileImage && (
                        <img
                          src={item.profileImage}
                          alt={item.displayName}
                          style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            marginRight: '8px',
                          }}
                        />
                      )}
                      <span>{item.displayName}</span>
                    </li>
                  );
                })}
              </ul>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLnDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              // Show the confirm modal
              setShowLnDeleteConfirmModal(true);
            }}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ************** LN Confirm Delete Modal ************** */}
      <Modal
        show={showLnDeleteConfirmModal}
        onHide={() => setShowLnDeleteConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete these LinkedIn personal accounts?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowLnDeleteConfirmModal(false)}
          >
            No
          </Button>
          <Button variant="danger" onClick={handleDeleteLnConfirm}>
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
